import { PersistentManager } from './PersistentManager';

const IMAGE_USAGE_KEY_PREFIX = 'image_usage_';

// Define image status types
export enum ImageStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed'
}

// Interface for the image usage data
export interface ImageUsageData {
  timestamp: number;
  status: ImageStatus;
}

/**
 * Manages image usage timestamps in persistent storage
 * This keeps track of when images were last used without modifying the Redux store
 */
export class ImageUsageManager {
  private static instance: ImageUsageManager;
  private usageCache: Record<string, Record<string, ImageUsageData>> = {};

  private constructor() {}

  public static getInstance(): ImageUsageManager {
    if (!ImageUsageManager.instance) {
      ImageUsageManager.instance = new ImageUsageManager();
    }
    return ImageUsageManager.instance;
  }

  /**
   * Update the usage timestamp for an image
   * @param categoryId The category ID
   * @param imageId The image ID
   * @param timestamp Optional timestamp (defaults to current time)
   * @param status Optional status (defaults to IN_PROGRESS)
   */
  public async updateImageUsage(
    categoryId: string, 
    imageId: string, 
    timestamp: number = Date.now(),
    status: ImageStatus = ImageStatus.IN_PROGRESS
  ): Promise<void> {
    try {
      // Get existing usage data for the category
      const usageData = await this.getCategoryUsage(categoryId);
      
      // Update the timestamp and status for this image
      usageData[imageId] = {
        timestamp,
        status
      };
      
      // Save back to persistent storage
      await PersistentManager.getInstance().setItem(
        this.getCategoryKey(categoryId), 
        JSON.stringify(usageData)
      );
      
      // Update the cache
      this.usageCache[categoryId] = usageData;
    } catch (error) {
      console.error('Failed to update image usage:', error);
    }
  }

  /**
   * Mark an image as completed
   * @param categoryId The category ID
   * @param imageId The image ID
   */
  public async markImageAsCompleted(categoryId: string, imageId: string): Promise<void> {
    await this.updateImageUsage(categoryId, imageId, Date.now(), ImageStatus.COMPLETED);
  }

  /**
   * Get the usage data for all images in a category
   * @param categoryId The category ID
   * @returns Record of image IDs to usage data
   */
  public async getCategoryUsage(categoryId: string): Promise<Record<string, ImageUsageData>> {
    try {
      // Check cache first
      if (this.usageCache[categoryId]) {
        return this.usageCache[categoryId];
      }
      
      // Get from storage
      const data = await PersistentManager.getInstance().getItem(this.getCategoryKey(categoryId));
      if (data) {
        try {
          const parsedData = JSON.parse(data);
          
          // Handle migration from old format (just timestamps) to new format (with status)
          const usageData: Record<string, ImageUsageData> = {};
          
          for (const [imageId, value] of Object.entries(parsedData)) {
            if (typeof value === 'number') {
              // Old format: convert to new format
              usageData[imageId] = {
                timestamp: value,
                status: ImageStatus.IN_PROGRESS
              };
            } else {
              // New format: already has status
              usageData[imageId] = value as ImageUsageData;
            }
          }
          
          // Update cache
          this.usageCache[categoryId] = usageData;
          return usageData;
        } catch (error) {
          console.error('Failed to parse category usage data:', error);
          return {};
        }
      }
      
      // Return empty object if no data exists
      return {};
    } catch (error) {
      console.error('Failed to get category usage data:', error);
      return {};
    }
  }

  /**
   * Get the timestamp when an image was last used
   * @param categoryId The category ID
   * @param imageId The image ID
   * @returns Timestamp or undefined if never used
   */
  public async getImageUsageTime(categoryId: string, imageId: string): Promise<number | undefined> {
    const usageData = await this.getCategoryUsage(categoryId);
    return usageData[imageId]?.timestamp;
  }
  
  /**
   * Get the status of an image
   * @param categoryId The category ID
   * @param imageId The image ID
   * @returns Status or undefined if never used
   */
  public async getImageStatus(categoryId: string, imageId: string): Promise<ImageStatus | undefined> {
    const usageData = await this.getCategoryUsage(categoryId);
    return usageData[imageId]?.status;
  }

  /**
   * Check if an image has been used
   * @param categoryId The category ID
   * @param imageId The image ID
   * @returns True if the image has been used before
   */
  public async isImageUsed(categoryId: string, imageId: string): Promise<boolean> {
    const usageData = await this.getCategoryUsage(categoryId);
    return !!usageData[imageId];
  }
  
  /**
   * Check if an image is completed
   * @param categoryId The category ID
   * @param imageId The image ID
   * @returns True if the image is completed
   */
  public async isImageCompleted(categoryId: string, imageId: string): Promise<boolean> {
    const status = await this.getImageStatus(categoryId, imageId);
    return status === ImageStatus.COMPLETED;
  }

  /**
   * Clear usage data for testing or reset purposes
   */
  public async clearAllUsageData(): Promise<void> {
    this.usageCache = {};
    // Could implement clearing all keys from storage if needed
  }

  private getCategoryKey(categoryId: string): string {
    return `${IMAGE_USAGE_KEY_PREFIX}${categoryId}`;
  }
}

export default ImageUsageManager; 